package net.sergeych.synctools

import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.InvocationKind
import kotlin.contracts.contract

/**
 * Multiplatform interface to perform a regular (not suspend) operation
 * protected by a platform mutex (where necessary). Get real implementation
 * with [ProtectedOp] and use it with [ProtectedOpImplementation.withLock] and
 * [ProtectedOpImplementation.invoke]
 */
interface ProtectedOpImplementation {
    /**
     * Get a lock. Be sure to release it.
     * The recommended way is using [ProtectedOpImplementation.withLock] and
     * [ProtectedOpImplementation.invoke]
     */
    fun lock()

    /**
     * Release a lock.
     * The recommended way is using [ProtectedOpImplementation.withLock] and
     * [ProtectedOpImplementation.invoke]
     */
    fun unlock()
}

@ExperimentalContracts
inline fun <T> ProtectedOpImplementation.withLock(f: () -> T): T {
    contract {
        callsInPlace(f, InvocationKind.EXACTLY_ONCE)
    }
    lock()
    return try {
        f()
    } finally {
        unlock()
    }
}

/**
 * Run a block mutualy-exclusively, see [ProtectedOp]
 */
operator fun <T> ProtectedOpImplementation.invoke(f: () -> T): T = withLock(f)

/**
 * Get the platform-depended implementation of a mutex. It does nothing in the
 * browser and use appropriate mechanics on JVM and native targets. See
 * [ProtectedOpImplementation.invoke], [ProtectedOpImplementation.withLock]
 * ```kotlin
 * val op = ProtectedOp()
 * //...
 * op {
 *      // mutually exclusive execution
 *      println("sequential execution here")
 * }
 * ~~~
 */
expect fun ProtectedOp(): ProtectedOpImplementation